import { cn } from '~/src/lib/utils'
import {
  renderHubSection,
  shouldRenderHubSection,
  type HubLayoutHint,
  type HubRenderContext,
  type HubSection,
} from '~/src/components/reports/hubs/kit'

/**
 * Canvas hub layout — the LLM controls section order and may emit a bounded
 * `layoutHint` per section. The hint is data, not CSS: the renderer maps each
 * value to an owned Tailwind class string defined right here.
 *
 * If the model emits a hint that isn't in `HINT_TO_CLASS`, we fall through to
 * `full`. The hint vocabulary is intentionally tiny — adding a new variant
 * means a code change + schema enum update in `packages/generative-ui`.
 */

const HINT_TO_CONTAINER_CLASS: Record<HubLayoutHint, string> = {
  full: 'col-span-12',
  twoColumn: 'col-span-12 md:col-span-6',
  featureAside: 'col-span-12 md:col-span-8',
  compactGrid: 'col-span-12 md:col-span-4',
  denseList: 'col-span-12 md:col-span-6 lg:col-span-4',
}

export interface CanvasHubLayoutProps {
  sections: HubSection[]
  context: HubRenderContext
}

export function CanvasHubLayout({ sections, context }: CanvasHubLayoutProps) {
  return (
    <article className='mx-auto w-full max-w-6xl px-4 pb-20 pt-8 md:px-10'>
      <div className='grid grid-flow-row-dense grid-cols-12 gap-x-6 gap-y-10 md:gap-y-12'>
        {sections.map((section) => {
          if (!shouldRenderHubSection(section, context)) return null

          const hint = section.layoutHint ?? 'full'
          const containerClass =
            HINT_TO_CONTAINER_CLASS[hint] ?? HINT_TO_CONTAINER_CLASS.full
          const renderedSection = renderHubSection(section, context)

          return (
            <div
              key={section.id}
              // min-w-0 keeps wide section content (comparison tables,
              // scrollers) from expanding the grid item past its column span.
              className={cn('min-w-0 scroll-mt-14', containerClass)}
            >
              {renderedSection}
            </div>
          )
        })}
      </div>
    </article>
  )
}
